import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '../../lib/utils';

function Dialog({ open, ...props }) {
  React.useEffect(() => {
    if (open) return;
    // Defensive workaround for a known Radix bug: when multiple Radix overlay
    // primitives (Dialog, DropdownMenu, Drawer) are mounted as siblings - e.g.
    // a DropdownMenu item opening a Dialog - and one closes - especially via an
    // outside click, or in the middle of another one's close transition -
    // Radix's scroll-lock can leave document.body stuck at pointer-events:none,
    // freezing every click on the page until a reload. If nothing is actually
    // open anymore, clear it. Checked across all overlay types, since whichever
    // one closes last is responsible for the cleanup.
    const timer = setTimeout(() => {
      const stillOpen = document.querySelector(
        '[data-slot="dialog-content"][data-state="open"], [data-slot="drawer-content"][data-state="open"], [data-slot="dropdown-menu-content"][data-state="open"]',
      );
      if (!stillOpen && document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open]);

  return <DialogPrimitive.Root data-slot="dialog" open={open} {...props} />;
}

function DialogTrigger({ ...props }) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

// Inline zIndex, not a Tailwind z-* class: this app has several fixed-position
// overlays (sidebar rail, bottom bars, "Attempt Similar Qs") that pin themselves
// above the page via inline style={{ zIndex }} up to 1001 — dialogs need to sit
// above all of those, and inline style avoids depending on Tailwind having
// regenerated a given arbitrary-value class.
function DialogOverlay({ className, style, ...props }) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50',
        'backdrop-blur-sm',
        className,
      )}
      style={{ zIndex: 1002, ...style }}
      {...props}
    />
  );
}

function DialogContent({ className, children, showCloseButton = true, style, ...props }) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          className,
        )}
        style={{ zIndex: 1003, ...style }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
